// #tag: ADMIN CONSOLE REST API CLIENT
// #purpose: Dispatches administrative API requests for user management, RBAC matrices, CMS pages, banner rotation, opportunities, audit trails, and AI diagnostics.
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export function fetchAdminMe() {
  return apiGet<import('../types/admin').AdminMeResponse>('/admin/me');
}

export function fetchAdminDashboard() {
  return apiGet<import('../types/admin').AdminDashboardResponse>('/admin/dashboard');
}

export function fetchAdminUsers(params?: {
  q?: string;
  role?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.role && params.role !== 'all') qs.set('role', params.role);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<import('../types/admin').AdminUsersResponse>(`/admin/users${query ? `?${query}` : ''}`);
}

export function fetchAdminUser(id: string) {
  return apiGet<import('../types/admin').AdminUserDetailResponse>(`/admin/users/${id}`);
}

export function createAdminUser(body: {
  name: string;
  email?: string;
  phone?: string;
  password: string;
  role?: string;
  status?: string;
}) {
  return apiPost<{ user: import('../types/admin').AdminUserRow }>('/admin/users', body);
}

export function patchAdminUser(id: string, body: { role?: string; status?: string; name?: string }) {
  return apiPatch<import('../types/admin').AdminUserRow>(`/admin/users/${id}`, body);
}

export function fetchAdminFaqs(params?: { q?: string; page?: string; status?: string }) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.page && params.page !== 'all') qs.set('page', params.page);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  const query = qs.toString();
  return apiGet<AdminFaqsResponse>(`/admin/content/faqs${query ? `?${query}` : ''}`);
}

export function fetchAdminBanners(params?: {
  q?: string;
  page?: string;
  type?: string;
  status?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.page && params.page !== 'all') qs.set('page', params.page);
  if (params?.type && params.type !== 'all') qs.set('type', params.type);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  const query = qs.toString();
  return apiGet<AdminBannersResponse>(`/admin/content/banners${query ? `?${query}` : ''}`);
}

export function fetchAdminPages() {
  return apiGet<AdminPagesResponse>('/admin/content/pages');
}

export function fetchAdminSections(params?: {
  page?: string;
  sectionType?: string;
  q?: string;
  status?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.page && params.page !== 'all') qs.set('page', params.page);
  if (params?.sectionType && params.sectionType !== 'all') qs.set('sectionType', params.sectionType);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.q) qs.set('q', params.q);
  const query = qs.toString();
  return apiGet<AdminSectionsResponse>(`/admin/content/sections${query ? `?${query}` : ''}`);
}

export function fetchAdminSection(id: string) {
  return apiGet<{ section: AdminSectionRow }>(`/admin/content/sections/${id}`);
}

export function patchAdminSection(id: string, body: Partial<AdminSectionRow>) {
  return apiPatch<AdminSectionRow>(`/admin/content/sections/${id}`, body);
}

export function fetchAdminNavigation(params?: {
  q?: string;
  context?: string;
  visible?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.context && params.context !== 'all') qs.set('context', params.context);
  if (params?.visible && params.visible !== 'all') qs.set('visible', params.visible);
  const query = qs.toString();
  return apiGet<{
    data: AdminNavigationRow[];
    stats: AdminNavigationStats;
    contexts: string[];
  }>(`/admin/content/navigation${query ? `?${query}` : ''}`);
}

export function fetchAdminNavigationItem(id: string) {
  return apiGet<{ item: AdminNavigationRow }>(`/admin/content/navigation/${id}`);
}

export function createAdminNavigation(body: {
  title: string;
  shortTitle: string;
  route: string;
  icon?: string;
  displayOrder?: number;
  permission?: string;
  visible?: boolean;
  context?: string;
}) {
  return apiPost<{ item: AdminNavigationRow }>('/admin/content/navigation', body);
}

export function patchAdminNavigation(id: string, body: Partial<AdminNavigationRow>) {
  return apiPatch<{ item: AdminNavigationRow }>(`/admin/content/navigation/${id}`, body);
}

export function fetchAdminResults(params?: {
  q?: string;
  category?: string;
  examCategory?: string;
  isNew?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.category && params.category !== 'all') qs.set('category', params.category);
  if (params?.examCategory && params.examCategory !== 'all') qs.set('examCategory', params.examCategory);
  if (params?.isNew && params.isNew !== 'all') qs.set('isNew', params.isNew);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminResultsResponse>(`/admin/results${query ? `?${query}` : ''}`);
}

export function fetchAdminResult(id: string) {
  return apiGet<{ result: AdminResultRow }>(`/admin/results/${id}`);
}

export function createAdminResult(body: {
  title: string;
  organization: string;
  examCategory: string;
  publishedAt: string;
  category?: string;
  resultUrl?: string;
  officialWebsite?: string;
  officialNotice?: string;
  isNew?: boolean;
}) {
  return apiPost<{ result: AdminResultRow }>('/admin/results', body);
}

export function patchAdminResult(id: string, body: Partial<AdminResultRow>) {
  return apiPatch<{ result: AdminResultRow }>(`/admin/results/${id}`, body);
}

export function fetchAdminOpportunities(params?: {
  q?: string;
  category?: string;
  status?: string;
  featured?: string;
  trending?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.category && params.category !== 'all') qs.set('category', params.category);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.featured && params.featured !== 'all') qs.set('featured', params.featured);
  if (params?.trending && params.trending !== 'all') qs.set('trending', params.trending);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminOpportunitiesResponse>(`/admin/opportunities${query ? `?${query}` : ''}`);
}

export function fetchAdminOpportunity(id: string) {
  return apiGet<{ opportunity: AdminOpportunityRow }>(`/admin/opportunities/${id}`);
}

export function createAdminOpportunity(body: {
  title: string;
  organization: string;
  category?: string;
  qualification?: string;
  state?: string;
  applicationStart?: string | null;
  applicationEnd?: string | null;
  status?: string;
  isFeatured?: boolean;
  isTrending?: boolean;
  slug?: string;
}) {
  return apiPost<{ opportunity: AdminOpportunityRow }>('/admin/opportunities', body);
}

export function patchAdminOpportunity(id: string, body: Partial<AdminOpportunityRow>) {
  return apiPatch<{ opportunity: AdminOpportunityRow }>(`/admin/opportunities/${id}`, body);
}

export function fetchAdminJobs(params?: {
  q?: string;
  status?: string;
  state?: string;
  featured?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.state && params.state !== 'all') qs.set('state', params.state);
  if (params?.featured && params.featured !== 'all') qs.set('featured', params.featured);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminJobsResponse>(`/admin/jobs${query ? `?${query}` : ''}`);
}

export function fetchAdminJob(id: string) {
  return apiGet<{ job: AdminOpportunityRow }>(`/admin/jobs/${id}`);
}

export function createAdminJob(body: {
  title: string;
  organization: string;
  qualification?: string;
  state?: string;
  department?: string;
  vacancies?: number | null;
  salary?: string;
  applicationStart?: string | null;
  applicationEnd?: string | null;
  status?: string;
  isFeatured?: boolean;
  officialApplyUrl?: string;
  slug?: string;
}) {
  return apiPost<{ job: AdminOpportunityRow }>('/admin/jobs', body);
}

export function fetchAdminAdmitCards() {
  return apiGet<{ data: AdminAdmitCardRow[] }>('/admin/admit-cards');
}

export function fetchAdminPlatformNotifications() {
  return apiGet<{ data: AdminNotificationRow[] }>('/admin/platform-notifications');
}

export function fetchAdminNotifications(params?: {
  q?: string;
  priority?: string;
  categoryId?: string;
  type?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.priority && params.priority !== 'all') qs.set('priority', params.priority);
  if (params?.categoryId && params.categoryId !== 'all') qs.set('categoryId', params.categoryId);
  if (params?.type && params.type !== 'all') qs.set('type', params.type);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminNotificationsResponse>(`/admin/notifications${query ? `?${query}` : ''}`);
}

export function fetchAdminNotification(id: string) {
  return apiGet<{ notification: AdminNotificationRow }>(`/admin/notifications/${id}`);
}

export function createAdminNotification(body: {
  title: string;
  message: string;
  priority?: string;
  categoryId?: string;
  type?: string;
  examId?: string;
  examTitle?: string;
  organization?: string;
  actionRoute?: string;
  actionLabel?: string;
}) {
  return apiPost<{ notification: AdminNotificationRow }>('/admin/notifications', body);
}

export function patchAdminNotification(id: string, body: Partial<AdminNotificationRow>) {
  return apiPatch<{ notification: AdminNotificationRow }>(`/admin/notifications/${id}`, body);
}

export function fetchAdminAnnouncements(params?: {
  q?: string;
  priority?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.priority && params.priority !== 'all') qs.set('priority', params.priority);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminAnnouncementsResponse>(`/admin/announcements${query ? `?${query}` : ''}`);
}

export function fetchAdminAnnouncement(id: string) {
  return apiGet<{ announcement: AdminAnnouncementRow }>(`/admin/announcements/${id}`);
}

export function createAdminAnnouncement(body: {
  title: string;
  message: string;
  priority?: string;
  actionRoute?: string;
  actionLabel?: string;
}) {
  return apiPost<{ announcement: AdminAnnouncementRow }>('/admin/announcements', body);
}

export function patchAdminAnnouncement(id: string, body: Partial<AdminAnnouncementRow>) {
  return apiPatch<AdminAnnouncementRow>(`/admin/announcements/${id}`, body);
}

export function fetchAdminMedia(params?: {
  q?: string;
  categoryId?: string;
  source?: string;
  type?: string;
  expiryStatus?: string;
  page?: number;
  limit?: number;
}) {
  const search = new URLSearchParams();
  if (params?.q) search.set('q', params.q);
  if (params?.categoryId) search.set('categoryId', params.categoryId);
  if (params?.source) search.set('source', params.source);
  if (params?.type) search.set('type', params.type);
  if (params?.expiryStatus) search.set('expiryStatus', params.expiryStatus);
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const query = search.toString();
  return apiGet<AdminMediaResponse>(`/admin/media${query ? `?${query}` : ''}`);
}

export function fetchAdminMediaAsset(id: string) {
  return apiGet<{ media: AdminMediaRow }>(`/admin/media/${id}`);
}

export function createAdminMedia(body: {
  name: string;
  categoryId: string;
  subcategory: string;
  description?: string;
  tags?: string[];
  fileName?: string;
  mimeType?: string;
  fileData?: string;
  expiryDate?: string | null;
}) {
  return apiPost<{ media: AdminMediaRow }>('/admin/media', body);
}

export function patchAdminMedia(id: string, body: Partial<AdminMediaRow>) {
  return apiPatch<{ media: AdminMediaRow }>(`/admin/media/${id}`, body);
}

export function fetchAdminSeo(params?: {
  q?: string;
  entityType?: string;
  completeness?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.entityType && params.entityType !== 'all') qs.set('entityType', params.entityType);
  if (params?.completeness && params.completeness !== 'all') qs.set('completeness', params.completeness);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminSeoResponse>(`/admin/seo${query ? `?${query}` : ''}`);
}

export function fetchAdminSeoEntry(entityType: string, id: string) {
  return apiGet<{ entry: AdminSeoEntry }>(`/admin/seo/${entityType}/${id}`);
}

export function patchAdminSeoEntry(entityType: string, id: string, body: { seoTitle?: string | null; seoDescription?: string | null }) {
  return apiPatch<{ entry: AdminSeoEntry }>(`/admin/seo/${entityType}/${id}`, body);
}

export function patchAdminSiteSeo(body: {
  seoTitle?: string | null;
  seoDescription?: string | null;
  metaKeywords?: string | null;
  tagline?: string | null;
}) {
  return apiPatch<{ site: AdminSiteSeo }>('/admin/seo/site', body);
}

export function fetchAdminAnalytics(params?: {
  period?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.period) qs.set('period', params.period);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminAnalyticsResponse>(`/admin/analytics${query ? `?${query}` : ''}`);
}

export function fetchAdminSupportTickets(params?: {
  q?: string;
  status?: string;
  priority?: string;
  category?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.priority && params.priority !== 'all') qs.set('priority', params.priority);
  if (params?.category && params.category !== 'all') qs.set('category', params.category);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminSupportTicketsResponse>(`/admin/support/tickets${query ? `?${query}` : ''}`);
}

export function fetchAdminSupportTicket(id: string) {
  return apiGet<{ ticket: AdminTicketRow }>(`/admin/support/tickets/${id}`);
}

export function createAdminSupportTicket(body: {
  subject: string;
  userEmail: string;
  description?: string;
  userName?: string;
  status?: string;
  priority?: string;
  category?: string;
  assignedTo?: string;
}) {
  return apiPost<{ ticket: AdminTicketRow }>('/admin/support/tickets', body);
}

export function patchAdminSupportTicket(id: string, body: Partial<AdminTicketRow>) {
  return apiPatch<{ ticket: AdminTicketRow }>(`/admin/support/tickets/${id}`, body);
}

export function fetchAdminAuditLogs(params?: {
  q?: string;
  module?: string;
  action?: string;
  userRole?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.module && params.module !== 'all') qs.set('module', params.module);
  if (params?.action && params.action !== 'all') qs.set('action', params.action);
  if (params?.userRole && params.userRole !== 'all') qs.set('userRole', params.userRole);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminAuditLogsResponse>(`/admin/audit-logs${query ? `?${query}` : ''}`);
}

export function fetchAdminAuditLog(id: string) {
  return apiGet<{ log: AdminAuditRow }>(`/admin/audit-logs/${id}`);
}

export function fetchAdminAi(params?: {
  q?: string;
  feature?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.feature && params.feature !== 'all') qs.set('feature', params.feature);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminAiResponse>(`/admin/ai${query ? `?${query}` : ''}`);
}

export function fetchAdminAiPrompt(id: string) {
  return apiGet<{ prompt: AdminAiPromptRow }>(`/admin/ai/prompts/${id}`);
}

export interface AdminAiAttachmentPayload {
  fileName: string;
  mimeType: string;
  kind: 'image' | 'document';
  dataBase64?: string;
  textContent?: string;
}

export function createAdminAiPrompt(body: {
  prompt: string;
  feature?: string;
  permission?: string;
  attachments?: AdminAiAttachmentPayload[];
}) {
  return apiPost<{ prompt: AdminAiPromptRow }>('/admin/ai/prompts', body);
}

export function patchAdminAiPrompt(id: string, body: { status?: string }) {
  return apiPatch<{ prompt: AdminAiPromptRow }>(`/admin/ai/prompts/${id}`, body);
}

export function rerunAdminAiPrompt(id: string) {
  return apiPost<{ prompt: AdminAiPromptRow }>(`/admin/ai/prompts/${id}/rerun`, {});
}

export function fetchAdminRoles() {
  return apiGet<import('../types/admin').AdminRolesResponse>('/admin/roles');
}

export function fetchAdminRole(id: string) {
  return apiGet<import('../types/admin').AdminRoleDetailResponse>(`/admin/roles/${id}`);
}

export function fetchAdminPermissions() {
  return apiGet<import('../types/admin').AdminPermissionsResponse>('/admin/permissions');
}

export function fetchAdminPermission(id: string) {
  return apiGet<import('../types/admin').AdminPermissionDetailResponse>(`/admin/permissions/${encodeURIComponent(id)}`);
}

export function fetchAdminSettings(params?: {
  q?: string;
  kind?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.kind && params.kind !== 'all') qs.set('kind', params.kind);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminSettingsResponse>(`/admin/settings${query ? `?${query}` : ''}`);
}

export function fetchAdminExamType(id: string) {
  return apiGet<{ examType: AdminExamTypeRow }>(`/admin/settings/exam-types/${id}`);
}

export function patchAdminExamType(id: string, body: Partial<AdminExamTypeRow>) {
  return apiPatch<{ examType: AdminExamTypeRow }>(`/admin/settings/exam-types/${id}`, body);
}

export function fetchAdminWidget(id: string) {
  return apiGet<{ widget: AdminWidgetRow }>(`/admin/settings/widgets/${id}`);
}

export function patchAdminWidget(id: string, body: Partial<AdminWidgetRow>) {
  return apiPatch<{ widget: AdminWidgetRow }>(`/admin/settings/widgets/${id}`, body);
}

export function fetchAdminVaultCategorySetting(id: string) {
  return apiGet<{ vaultCategory: AdminVaultCategoryRow }>(`/admin/settings/vault-categories/${id}`);
}

export function patchAdminVaultCategorySetting(id: string, body: Partial<AdminVaultCategoryRow>) {
  return apiPatch<{ vaultCategory: AdminVaultCategoryRow }>(`/admin/settings/vault-categories/${id}`, body);
}

export function patchAdminSiteConfig(key: string, value: Record<string, unknown>) {
  return apiPatch<{ key: string; value: Record<string, unknown> }>(`/admin/settings/site-config/${key}`, { value });
}

export function fetchAdminProfile(params?: {
  q?: string;
  group?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.group && params.group !== 'all') qs.set('group', params.group);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminProfileResponse>(`/admin/profile${query ? `?${query}` : ''}`);
}

export function fetchAdminProfilePermission(id: string) {
  return apiGet<{ permission: AdminProfilePermissionRow }>(`/admin/profile/permissions/${encodeURIComponent(id)}`);
}

export function fetchAdminProfileActivity(id: string) {
  return apiGet<{ activity: AdminProfileActivityRow }>(`/admin/profile/activity/${id}`);
}

export function patchAdminProfile(body: { name?: string }) {
  return apiPatch<{ user: AdminProfileUser }>('/admin/profile', body);
}

export function fetchAdminCalendar() {
  return apiGet<{ data: AdminCalendarRow[] }>('/admin/calendar');
}

export function fetchAdminAcademy(params?: {
  q?: string;
  type?: string;
  opportunityId?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.type && params.type !== 'all') qs.set('type', params.type);
  if (params?.opportunityId && params.opportunityId !== 'all') qs.set('opportunityId', params.opportunityId);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminAcademyResponse>(`/admin/academy${query ? `?${query}` : ''}`);
}

export function fetchAdminAcademyModule(id: string) {
  return apiGet<{ module: AdminAcademyModuleRow }>(`/admin/academy/modules/${id}`);
}

export function createAdminAcademyModule(body: {
  opportunityId: string;
  label: string;
  url: string;
  type?: string;
}) {
  return apiPost<{ module: AdminAcademyModuleRow }>('/admin/academy/modules', body);
}

export function patchAdminAcademyModule(id: string, body: Partial<Pick<AdminAcademyModuleRow, 'label' | 'url' | 'type'>>) {
  return apiPatch<{ module: AdminAcademyModuleRow }>(`/admin/academy/modules/${id}`, body);
}

export function deleteAdminAcademyModule(id: string) {
  return apiDelete(`/admin/academy/modules/${id}`);
}

export function fetchAdminColleges(params?: {
  q?: string;
  state?: string;
  type?: string;
  status?: string;
  featured?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.state && params.state !== 'all') qs.set('state', params.state);
  if (params?.type && params.type !== 'all') qs.set('type', params.type);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.featured && params.featured !== 'all') qs.set('featured', params.featured);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminCollegesResponse>(`/admin/colleges${query ? `?${query}` : ''}`);
}

export function fetchAdminCollege(id: string) {
  return apiGet<{ college: AdminCollegeRow }>(`/admin/colleges/${id}`);
}

export function createAdminCollege(body: {
  name: string;
  state: string;
  city?: string;
  type?: string;
  courses?: number;
  status?: string;
  website?: string;
  description?: string;
  establishedYear?: number | null;
  accreditation?: string;
  isFeatured?: boolean;
  slug?: string;
}) {
  return apiPost<{ college: AdminCollegeRow }>('/admin/colleges', body);
}

export function patchAdminCollege(id: string, body: Partial<AdminCollegeRow>) {
  return apiPatch<{ college: AdminCollegeRow }>(`/admin/colleges/${id}`, body);
}

export function fetchAdminBlogs(params?: {
  q?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiGet<AdminBlogsResponse>(`/admin/blogs${query ? `?${query}` : ''}`);
}

export function fetchAdminBlog(id: string) {
  return apiGet<{ post: AdminBlogRow }>(`/admin/blogs/${id}`);
}

export function createAdminBlog(body: {
  title: string;
  author: string;
  excerpt?: string;
  content?: string;
  status?: string;
  publishedAt?: string | null;
  slug?: string;
  seoTitle?: string;
  seoDescription?: string;
}) {
  return apiPost<{ post: AdminBlogRow }>('/admin/blogs', body);
}

export function patchAdminBlog(id: string, body: Partial<AdminBlogRow>) {
  return apiPatch<AdminBlogRow>(`/admin/blogs/${id}`, body);
}

export interface AdminSectionRow {
  id: string;
  page: string;
  pageLabel?: string;
  pageRoute?: string;
  sectionType: string;
  sectionTypeLabel?: string;
  title?: string | null;
  subtitle?: string | null;
  displayOrder: number;
  isEnabled: boolean;
  configuration?: Record<string, unknown>;
}

export interface AdminPageSummary {
  id: string;
  label: string;
  route: string;
  illustration: string;
  sectionCount: number;
  enabledCount: number;
  disabledCount: number;
  sectionTypes: string[];
}

export interface AdminPagesResponse {
  pages: AdminPageSummary[];
  stats: AdminCmsStats;
}

export interface AdminSectionsResponse {
  data: AdminSectionRow[];
  stats: AdminCmsStats;
}

export interface AdminCmsStats {
  total: number;
  enabled: number;
  disabled: number;
  pages: number;
}

export interface AdminFaqSectionRow extends AdminSectionRow {
  questionCount: number;
  visualTypes: string[];
}

export interface AdminFaqItemRow {
  id: string;
  sectionId: string;
  index: number;
  page: string;
  pageLabel: string;
  pageRoute: string;
  sectionTitle?: string | null;
  question: string;
  answer: string;
  icon?: string | null;
  visual?: string | null;
  isEnabled: boolean;
}

export interface AdminFaqsResponse {
  sections: AdminFaqSectionRow[];
  items: AdminFaqItemRow[];
  stats: {
    sections: number;
    questions: number;
    enabled: number;
    pages: number;
  };
}

export interface AdminBannerRow extends AdminSectionRow {
  kicker?: string | null;
  illustration?: string | null;
  primaryCtaLabel?: string | null;
  primaryCtaRoute?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaRoute?: string | null;
}

export interface AdminBannerStats {
  total: number;
  enabled: number;
  disabled: number;
  heroes: number;
  ctaBanners: number;
  pageHeaders: number;
  pages: number;
}

export interface AdminBannersResponse {
  data: AdminBannerRow[];
  stats: AdminBannerStats;
  pages: Array<{ id: string; label: string; route: string }>;
}

export interface AdminNavigationRow {
  id: string;
  title: string;
  shortTitle: string;
  route: string;
  icon: string;
  displayOrder: number;
  visible: boolean;
  permission: string;
  context: string;
  parentId?: string | null;
}

export interface AdminNavigationStats {
  total: number;
  visible: number;
  hidden: number;
  authenticated: number;
  contexts: number;
}

export interface AdminResultRow {
  id: string;
  title: string;
  organization: string;
  category: string;
  examCategory: string;
  publishedAt?: string;
  resultUrl?: string | null;
  officialNotice?: string | null;
  officialWebsite?: string | null;
  hasScoreCard?: boolean;
  hasMeritList?: boolean;
  hasCutoff?: boolean;
  hasCounselling?: boolean;
  isNew?: boolean;
  sourceUrl?: string | null;
  sourceName?: string | null;
  importantDates?: Array<{ label: string; date: string }>;
}

export interface AdminResultStats {
  total: number;
  new: number;
  central: number;
  state: number;
  entrance: number;
  recruitment: number;
}

export interface AdminResultsResponse {
  data: AdminResultRow[];
  stats: AdminResultStats;
  categories: string[];
  examCategories: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminOpportunityRow {
  id: string;
  title: string;
  slug: string;
  organization: string;
  category: string;
  opportunityType?: string;
  qualification: string;
  state?: string;
  location?: string | null;
  department?: string | null;
  vacancies?: number | null;
  salary?: string | null;
  applicationStart?: string | null;
  applicationEnd?: string | null;
  examDate?: string | null;
  status: string;
  officialNotification?: string | null;
  officialWebsite?: string | null;
  officialApplyUrl?: string | null;
  eligibility?: string | null;
  description?: string | null;
  applicationFee?: string | null;
  ageLimit?: string | null;
  isFeatured?: boolean;
  isTrending?: boolean;
  viewCount?: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
}

export interface AdminOpportunityStats {
  total: number;
  open: number;
  closed: number;
  featured: number;
  trending: number;
  closingSoon: number;
}

export interface AdminOpportunitiesResponse {
  data: AdminOpportunityRow[];
  stats: AdminOpportunityStats;
  categories: string[];
  qualifications: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminJobStats {
  total: number;
  open: number;
  closed: number;
  featured: number;
  vacancies: number;
  states: number;
}

export interface AdminJobsResponse {
  data: AdminOpportunityRow[];
  stats: AdminJobStats;
  qualifications: string[];
  states: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminAdmitCardRow {
  id: string;
  title: string;
  organization: string;
  status: string;
  releaseDate?: string;
  examDate?: string;
}

export interface AdminNotificationRow {
  id: string;
  title: string;
  message: string;
  type: string;
  categoryId?: string | null;
  categoryLabel?: string;
  priority: string;
  examId?: string | null;
  examTitle?: string | null;
  organization?: string | null;
  actionRoute?: string | null;
  actionLabel?: string | null;
  createdAt: string;
}

export interface AdminNotificationsResponse {
  data: AdminNotificationRow[];
  stats: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    categories: number;
  };
  categories: Array<{ id: string; label: string }>;
  priorities: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminAnnouncementRow {
  id: string;
  title: string;
  message: string;
  type: string;
  categoryId?: string | null;
  priority: string;
  actionRoute?: string | null;
  actionLabel?: string | null;
  createdAt: string;
}

export interface AdminAnnouncementsResponse {
  data: AdminAnnouncementRow[];
  stats: {
    total: number;
    critical: number;
    high: number;
    medium: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminMediaRow {
  id: string;
  name: string;
  categoryId: string;
  subcategory: string;
  mimeType?: string | null;
  fileSize?: string | null;
  fileName?: string | null;
  uploadedAt: string;
  expiryDate?: string | null;
  expiryStatus?: string | null;
  tags: string[];
  description?: string | null;
  userId: string;
  source: 'platform' | 'student';
  ownerName?: string | null;
  ownerEmail?: string | null;
  isImage: boolean;
  version: number;
  createdAt?: string;
}

export interface AdminMediaStats {
  total: number;
  platform: number;
  student: number;
  images: number;
  categories: number;
  estimatedMb: number;
}

export interface AdminMediaCategory {
  id: string;
  label: string;
}

export interface AdminMediaResponse {
  data: AdminMediaRow[];
  stats: AdminMediaStats;
  categories: AdminMediaCategory[];
  sources: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminSeoOpportunity {
  id: string;
  title: string;
  slug: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
}

export interface AdminSeoEntry {
  id: string;
  entityType: 'opportunity' | 'blog';
  title: string;
  slug: string;
  status?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  hasSeoTitle: boolean;
  hasSeoDescription: boolean;
}

export interface AdminSiteSeo {
  name?: string | null;
  tagline?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  metaKeywords?: string | null;
}

export interface AdminSeoResponse {
  site: AdminSiteSeo;
  data: AdminSeoEntry[];
  stats: {
    total: number;
    opportunities: number;
    blogs: number;
    missingTitle: number;
    missingDescription: number;
    complete: number;
  };
  entityTypes: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminAnalyticsActivity {
  id: string;
  type: 'registration' | 'notification';
  label: string;
  detail?: string;
  meta?: string;
  timestamp: string;
}

export interface AdminAnalyticsResponse {
  period: string;
  users: { total: number; today: number; week: number; month: number };
  content: {
    results: number;
    openOpportunities: number;
    vaultDocuments: number;
    publishedBlogs: number;
    notificationsInPeriod: number;
  };
  engagement: { score: number };
  traffic: {
    today: number;
    yesterday: number;
    lastWeek: number;
    sources: Array<{ label: string; value: number }>;
    estimated?: boolean;
  };
  topModules: Array<{ label: string; value: number }>;
  activity: AdminAnalyticsActivity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminTicketRow {
  id: string;
  subject: string;
  description?: string | null;
  userEmail: string;
  userName?: string | null;
  status: string;
  priority: string;
  category: string;
  assignedTo?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface AdminSupportTicketsResponse {
  data: AdminTicketRow[];
  stats: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    resolvedToday: number;
    urgent: number;
    avgResponseMinutes: number;
  };
  statuses: string[];
  priorities: string[];
  categories: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminAuditRow {
  id: string;
  userId?: string | null;
  userName: string;
  userRole?: string | null;
  action: string;
  module: string;
  resourceId?: string | null;
  details?: string | null;
  ipAddress?: string | null;
  createdAt: string;
}

export interface AdminAuditLogsResponse {
  data: AdminAuditRow[];
  stats: {
    total: number;
    today: number;
    modules: number;
    actors: number;
  };
  modules: string[];
  actions: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminAiFeature {
  id: string;
  label: string;
  description: string;
}

export interface AdminAiPromptRow {
  id: string;
  userId?: string | null;
  userName: string;
  prompt: string;
  promptPreview?: string;
  response?: string | null;
  responsePreview?: string | null;
  responseLength?: number;
  feature: string;
  featureLabel?: string;
  featureDescription?: string;
  status: string;
  createdAt: string;
}

export interface AdminAiFeatureCount {
  id: string;
  label: string;
  count: number;
}

export interface AdminAiPermissionOption {
  id: string;
  label: string;
}

export interface AdminAiResponse {
  enabled: boolean;
  version: string;
  model: string;
  features: AdminAiFeature[];
  statuses: string[];
  permissions: AdminAiPermissionOption[];
  featureCounts: AdminAiFeatureCount[];
  data: AdminAiPromptRow[];
  stats: {
    total: number;
    completed: number;
    pending: number;
    failed: number;
    today: number;
    features: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminExamTypeRow {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  themeColor?: string;
  isActive: boolean;
  displayOrder: number;
  supportsResults?: boolean;
  supportsAdmitCards?: boolean;
  supportsMockTests?: boolean;
  supportsPreviousYear?: boolean;
}

export interface AdminWidgetRow {
  id: string;
  title?: string | null;
  widgetType: string;
  isEnabled: boolean;
  displayOrder: number;
  visibility: string;
  configuration?: Record<string, unknown>;
}

export interface AdminVaultCategoryRow {
  id: string;
  label: string;
  isActive: boolean;
  displayOrder: number;
  icon: string;
  itemCount?: number;
  items?: string[];
}

export interface AdminSettingEntry {
  id: string;
  kind: 'examType' | 'widget' | 'vaultCategory';
  title: string;
  subtitle: string;
  status: string;
  displayOrder: number;
  meta: Record<string, unknown>;
  examType?: AdminExamTypeRow;
  widget?: AdminWidgetRow;
  vaultCategory?: AdminVaultCategoryRow;
}

export interface AdminSettingsResponse {
  siteConfig: Record<string, unknown>;
  platform: Record<string, unknown>;
  footer: Record<string, unknown>;
  examTypes: AdminExamTypeRow[];
  widgets: AdminWidgetRow[];
  vaultCategories: AdminVaultCategoryRow[];
  data: AdminSettingEntry[];
  stats: {
    configKeys: number;
    total: number;
    examTypes: number;
    activeExamTypes: number;
    widgets: number;
    enabledWidgets: number;
    vaultCategories: number;
    activeVaultCategories: number;
  };
  kinds: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminProfilePermissionRow {
  id: string;
  permission: string;
  label: string;
  group: string;
  status: string;
}

export interface AdminProfileActivityRow {
  id: string;
  action: string;
  module: string;
  resourceId?: string | null;
  details?: string | null;
  ipAddress?: string | null;
  createdAt: string;
}

export interface AdminProfileUser {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  status: string;
  createdAt: string;
}

export interface AdminProfileResponse {
  user: AdminProfileUser;
  permissions: import('../types/admin').AdminPermission[];
  permissionGroups: Array<{ group: string; permissions: Array<{ id: string; label: string }> }>;
  data: AdminProfilePermissionRow[];
  groups: string[];
  recentActivity: AdminProfileActivityRow[];
  isSuperAdmin: boolean;
  roleLabel: string;
  roleDescription?: string | null;
  roleMeta?: { tier: string; rank: number } | null;
  stats: {
    permissionCount: number;
    groupCount: number;
    profileCompleteness: number;
    recentActivityCount: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminPermissionRow {
  permission: string;
  label?: string;
  roles: string[];
}

export interface AdminCalendarRow {
  id: string;
  title: string;
  type: string;
  date: string;
}

export interface AdminAcademyModuleRow {
  id: string;
  opportunityId: string;
  opportunityTitle: string;
  opportunitySlug: string;
  opportunityCategory: string;
  index: number;
  label: string;
  url: string;
  type: string;
  typeLabel: string;
  isCustom: boolean;
}

export interface AdminAcademyStats {
  modules: number;
  opportunities: number;
  customSets: number;
  types: number;
}

export interface AdminAcademyResponse {
  data: AdminAcademyModuleRow[];
  stats: AdminAcademyStats;
  opportunities: Array<{ id: string; title: string; slug: string; category: string }>;
  types: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** @deprecated use AdminAcademyModuleRow */
export interface AdminAcademyRow {
  id: string;
  title: string;
  opportunity: string;
  url?: string;
}

export interface AdminCollegeRow {
  id: string;
  name: string;
  slug: string;
  state: string;
  city?: string | null;
  type: string;
  typeLabel?: string;
  courses: number;
  status: string;
  website?: string | null;
  description?: string | null;
  establishedYear?: number | null;
  accreditation?: string | null;
  isFeatured?: boolean;
}

export interface AdminCollegeStats {
  total: number;
  published: number;
  draft: number;
  featured: number;
  states: number;
}

export interface AdminCollegesResponse {
  data: AdminCollegeRow[];
  stats: AdminCollegeStats;
  types: string[];
  states: string[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AdminBlogRow {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  author: string;
  status: string;
  publishedAt: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminBlogsResponse {
  data: AdminBlogRow[];
  stats: {
    total: number;
    published: number;
    draft: number;
    scheduled: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}
